import { formatPrice } from '../../api';
import type { FamilyChild } from '../../api';

// Built-in columns get sentinel keys so they cannot collide with A4's
// `a4_<rel_id>` dynamic selector columns.
export const COL_PARTNUM = '__partnum__';
export const COL_PRICE = '__price__';
export const COL_SHIPS = '__ships__';

export interface ColumnDef {
  key: string;
  label: string;
  sortable: boolean;
  filterable: boolean;
}

export type SortDirection = 'asc' | 'desc';
export interface SortState { key: string; dir: SortDirection }
export type FilterState = Record<string, Set<string>>;
export type FilterOptionsByKey = Map<string, string[]>;
export type ColumnAlign = 'left' | 'center' | 'right';

export function getColumnWidthClass(column: ColumnDef): string {
  if (column.key === COL_PARTNUM) return 'ts-family-col--partnum';
  if (column.key === COL_PRICE) return 'ts-family-col--price';
  if (column.key === COL_SHIPS) return 'ts-family-col--ships';

  const label = column.label.toLowerCase();
  if (label.includes('material')) return 'ts-family-col--material';
  if (label.includes('iso') || label.includes('certified')) return 'ts-family-col--iso';
  if (label.includes('chamber') || label.includes('compartment') || label.startsWith('number of')) {
    return 'ts-family-col--count';
  }
  if (
    label === 'width' ||
    label === 'depth' ||
    label === 'height' ||
    label.includes(' width') ||
    label.includes(' depth') ||
    label.includes(' height')
  ) {
    return 'ts-family-col--metric';
  }
  if (label.includes('voltage') || label.includes('electrical')) return 'ts-family-col--short';
  if (label.includes('price') || label.includes('ship') || label.includes('stock')) return 'ts-family-col--short';
  return 'ts-family-col--default';
}

function getChildStocked(child: FamilyChild): boolean {
  return !!child.stocked || (child.availability_status || '').toLowerCase().startsWith('in_stock');
}

export function cleanAttribute(value: string | number | null | undefined): string {
  const cleaned = String(value ?? '').replace(/\s+/g, ' ').trim();
  return cleaned || '-';
}

function addUniqueAttribute(values: string[], value: string | null | undefined): void {
  const cleaned = cleanAttribute(value);
  if (cleaned === '-') return;
  if (values.some((existing) => existing.toLowerCase() === cleaned.toLowerCase())) return;
  values.push(cleaned);
}

export function formatCertifications(child: FamilyChild): string {
  const values: string[] = [];
  (child.certifications || '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
    .forEach((value) => addUniqueAttribute(values, value));
  (child.iso_classes || []).forEach((value) => addUniqueAttribute(values, value));
  return values.length > 0 ? values.join(', ') : '-';
}

export function formatLeadTime(child: FamilyChild): string {
  const leadTime = cleanAttribute(child.lead_time_text);
  if (leadTime !== '-') return leadTime;
  if (getChildStocked(child)) return 'In stock';
  return 'Check lead time';
}

function isReadyLeadTime(leadTime: string): boolean {
  const normalized = leadTime.trim().toLowerCase().replace(/[–—]/g, '-').replace(/\s+/g, ' ');
  if (!normalized || normalized === '-') return false;
  if (normalized.includes('in stock') || normalized.includes('ready')) return true;
  return /^(?:0\s*-\s*2|0\s*-\s*3|1\s*-\s*3|1\s*-\s*5|2\s*-\s*3)\s*(?:business\s*)?days?$/.test(normalized);
}

export function isChildReadyNow(child: FamilyChild): boolean {
  return getChildStocked(child) || isReadyLeadTime(formatLeadTime(child));
}

export function getCellValue(child: FamilyChild, columnKey: string): string {
  switch (columnKey) {
    case COL_PARTNUM:
      return child.part_number || '';
    case COL_PRICE: {
      const p = child.price;
      return p != null && p > 0 ? formatPrice(p) : 'Request Quote';
    }
    case COL_SHIPS:
      return formatLeadTime(child);
    case '__name__':
      return cleanAttribute(child.name);
    case '__dimensions__':
      return cleanAttribute(child.dimensions);
    case '__electrical__':
      return cleanAttribute(child.electrical_specs);
    case '__certifications__':
      return formatCertifications(child);
    default:
      return cleanAttribute(child.selector_values?.[columnKey]);
  }
}

export function getCellSortKey(child: FamilyChild, columnKey: string): number | string {
  if (columnKey === COL_PRICE) {
    return child.price != null && child.price > 0 ? child.price : Number.POSITIVE_INFINITY;
  }
  if (columnKey === COL_SHIPS) {
    return isChildReadyNow(child) ? `0|${formatLeadTime(child)}` : `1|${formatLeadTime(child)}`;
  }
  const raw = getCellValue(child, columnKey).trim();
  if (!raw || raw === '-') return Number.POSITIVE_INFINITY;
  const numMatch = raw.match(/-?\d+(?:\.\d+)?/);
  if (numMatch && (raw.startsWith(numMatch[0]) || /^[A-Za-z]+\s*\d/.test(raw))) {
    const n = parseFloat(numMatch[0]);
    if (Number.isFinite(n)) return n;
  }
  return raw.toLowerCase();
}

export function compareSortKeys(a: number | string, b: number | string): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

export function getDistinctValues(children: FamilyChild[], columnKey: string): string[] {
  const seen = new Map<string, string>();
  for (const child of children) {
    const value = getCellValue(child, columnKey);
    if (!value || value === '-') continue;
    const key = value.toLowerCase();
    if (!seen.has(key)) seen.set(key, value);
  }
  return Array.from(seen.values()).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );
}

export function minWidthForColumn(label: string, key: string, children: FamilyChild[]): number {
  const words = label.split(/\s+/).filter(Boolean);
  const longestWord = words.length > 0
    ? Math.max(...words.map((word) => word.length))
    : label.length;
  let maxContentLen = label.length;
  for (const child of children) {
    const value = getCellValue(child, key);
    if (value.length > maxContentLen) maxContentLen = value.length;
  }

  const headerFloor = Math.max(longestWord * 8.4 + 44, Math.min(label.length * 6.8 + 44, 240));
  const contentWidth = Math.min(maxContentLen, 26) * 7.2 + 34;
  const floor =
    key === COL_PARTNUM ? 132 :
    key === COL_PRICE ? 118 :
    key === COL_SHIPS ? 134 :
    116;
  const ceiling =
    key === COL_PARTNUM ? 156 :
    key === COL_PRICE ? 132 :
    key === COL_SHIPS ? 154 :
    /material|certified|certification|electrical|dimension|grade|class/i.test(label) ? 260 :
    220;

  return Math.round(Math.max(floor, Math.min(ceiling, Math.max(headerFloor, contentWidth))));
}

export function getColumnAlignment(column: ColumnDef, children: FamilyChild[]): ColumnAlign {
  if (column.key === COL_PARTNUM) return 'left';
  if (column.key === COL_PRICE) return 'center';
  if (column.key === COL_SHIPS) return 'center';
  if (column.key === '__name__') return 'left';

  const label = column.label.toLowerCase();
  if (label.includes('material') || label.includes('finish') || label.includes('description')) {
    return 'left';
  }

  const values = children
    .map((child) => getCellValue(child, column.key))
    .filter((value) => value && value !== '-');
  if (values.length === 0) return 'left';

  const compactCount = values.filter((value) => value.length <= 12).length;
  const avgLen = values.reduce((sum, value) => sum + value.length, 0) / values.length;
  const allNumericLike = values.every((value) =>
    /^(?:iso\s*)?\d+(?:[./-]\d+)?(?:\s*(?:v|hz|amp|a|w|kw|lb|lbs|in|ft|mm|cm|m|%|cfm|fpm|rpm))?$/i.test(value.trim()),
  );

  if (label.includes('iso') || label.includes('certif') || label.includes('grade') || label.includes('class')) {
    return 'center';
  }
  if (allNumericLike) return 'center';
  if (compactCount / values.length >= 0.75 && avgLen <= 14) return 'center';
  return 'left';
}
